<template>
    <b-modal
        v-model="showModal"
        size="lg"
        centered
        scrollable
        header-class="elite-detail-modal-header"
        footer-class="elite-detail-modal-footer"
        body-class="p-0"
        class="activity-config-detail-modal"
        @hide="onHide"
    >
        <template #modal-header="{ close }">
            <div class="elite-header-inner">
                <span class="elite-title-inner">배출원 설정 상세</span>
                <div v-if="config" class="elite-badges">
                    <span class="elite-badge elite-badge-id">ID #{{ config.configIdx }}</span>
                    <span
                        class="elite-badge"
                        :class="{
                            'elite-badge-active': config.draftStat === 'ACTIVE',
                            'elite-badge-draft': config.draftStat === 'DRAFT',
                            'elite-badge-inactive': config.draftStat === 'INACTIVE',
                        }"
                    >
                        <span class="elite-badge-dot" v-if="config.draftStat === 'ACTIVE'"></span>
                        {{ draftStatLabel }}
                    </span>
                    <span class="elite-badge elite-badge-method">{{ collectionMethodLabel }}</span>
                </div>
                <b-button variant="link" class="elite-header-close p-0 ml-auto" aria-label="닫기" @click="close()">
                    <i class="fas fa-times"></i>
                </b-button>
            </div>
        </template>

        <!-- 단일 루트: scoped 스타일이 우리가 렌더한 요소에만 적용되도록 래퍼 사용 -->
        <div v-if="loading" class="elite-loading">
            <i class="fas fa-spinner fa-spin fa-2x"></i>
            <p>상세 정보를 불러오는 중...</p>
        </div>
        <div v-else-if="config" class="elite-detail-wrap">
            <!-- Main content -->
            <main class="elite-main custom-scrollbar">
                <div class="elite-sections">
                    <!-- Section 1: Facility Info -->
                    <section class="elite-section">
                        <div class="elite-section-head">
                            <div class="elite-section-bar"></div>
                            <h2 class="elite-section-title">배출 시설 정보</h2>
                        </div>
                        <div class="elite-facility-grid">
                            <div class="elite-card">
                                <div class="elite-card-label">조직</div>
                                <div class="elite-card-value">{{ config.organization?.orgName || "—" }}</div>
                            </div>
                            <div class="elite-card">
                                <div class="elite-card-label">시설명</div>
                                <div class="elite-card-value">
                                    {{ config.facility?.facilityName || "전체" }}
                                </div>
                            </div>
                            <div class="elite-card">
                                <div class="elite-card-label">활동 분류</div>
                                <div class="elite-card-value">{{ config.category?.categoryName || "—" }}</div>
                            </div>
                        </div>
                    </section>

                    <!-- Section 2: General Settings -->
                    <section class="elite-section">
                        <div class="elite-section-head">
                            <div class="elite-section-bar"></div>
                            <h2 class="elite-section-title">설정 정보</h2>
                        </div>
                        <div class="elite-settings-grid">
                            <div class="elite-setting">
                                <div class="elite-setting-label">사용 기간</div>
                                <div class="elite-setting-value">
                                    {{ config.startMonth || "—" }} ~ {{ config.endMonth || "무기한" }}
                                </div>
                            </div>
                            <div class="elite-setting">
                                <div class="elite-setting-label">보고 주기</div>
                                <div class="elite-setting-value">{{ reportCycleLabel }}</div>
                            </div>
                            <div class="elite-setting">
                                <div class="elite-setting-label">수집 방법</div>
                                <div class="elite-setting-value">{{ collectionMethodLabel }}</div>
                            </div>
                            <div class="elite-setting">
                                <div class="elite-setting-label">에너지원</div>
                                <div class="elite-setting-value elite-setting-fuel">
                                    <span class="elite-fuel-dot"></span>
                                    {{ preferredFuelLabel }}
                                </div>
                            </div>
                            <div class="elite-setting">
                                <div class="elite-setting-label">측정 단위</div>
                                <div class="elite-setting-value">{{ config.preferredUnit || "—" }}</div>
                            </div>
                            <div v-if="config.isAutoCollect === 1" class="elite-setting">
                                <div class="elite-setting-label">자동 수집</div>
                                <div class="elite-setting-value">활성</div>
                            </div>
                            <template v-if="config.isAutoCollect === 1">
                                <div v-if="config.collectSchedule || config.collectCycle" class="elite-setting">
                                    <div class="elite-setting-label">수집 주기</div>
                                    <div class="elite-setting-value">
                                        {{ config.collectSchedule || config.collectCycle }}
                                    </div>
                                </div>
                                <div
                                    v-if="config.collectSchedule === 'monthly' || config.collectCycle === 'monthly'"
                                    class="elite-setting"
                                >
                                    <div class="elite-setting-label">수집일</div>
                                    <div class="elite-setting-value">{{ config.collectDay || "—" }}일</div>
                                </div>
                                <div
                                    v-if="
                                        config.collectSchedule === 'daily' ||
                                        config.collectSchedule === 'monthly' ||
                                        config.collectCycle === 'daily' ||
                                        config.collectCycle === 'monthly'
                                    "
                                    class="elite-setting"
                                >
                                    <div class="elite-setting-label">수집 시간</div>
                                    <div class="elite-setting-value">{{ config.collectTime || "—" }}</div>
                                </div>
                            </template>
                            <div v-if="config.dataSource" class="elite-setting">
                                <div class="elite-setting-label">데이터 소스</div>
                                <div class="elite-setting-value">{{ dataSourceLabel }}</div>
                            </div>
                        </div>
                        <div v-if="ptAddrsDisplay || config.apiEndpoint" class="elite-settings-sub">
                            <div v-if="ptAddrsDisplay" class="elite-setting elite-setting-full">
                                <div class="elite-setting-label">관제점</div>
                                <div class="elite-setting-value elite-mono">{{ ptAddrsDisplay }}</div>
                            </div>
                            <div v-if="config.apiEndpoint" class="elite-setting elite-setting-full">
                                <div class="elite-setting-label">API 엔드포인트</div>
                                <div class="elite-setting-value elite-mono">{{ config.apiEndpoint }}</div>
                            </div>
                            <div v-if="config.apiMethod" class="elite-setting">
                                <div class="elite-setting-label">API 메서드</div>
                                <div class="elite-setting-value">{{ config.apiMethod }}</div>
                            </div>
                            <div v-if="apiHeadersDisplay" class="elite-setting elite-setting-full">
                                <div class="elite-setting-label">API 헤더</div>
                                <div class="elite-setting-value elite-mono pre-wrap">{{ apiHeadersDisplay }}</div>
                            </div>
                            <div v-if="config.apiResponsePath" class="elite-setting elite-setting-full">
                                <div class="elite-setting-label">API 응답 경로</div>
                                <div class="elite-setting-value elite-mono">{{ config.apiResponsePath }}</div>
                            </div>
                        </div>
                    </section>

                    <!-- Section 3: Emission Factors -->
                    <section v-if="config.factorIdx" class="elite-section">
                        <div v-if="factorLoading" class="elite-factor-loading">
                            <i class="fas fa-spinner fa-spin"></i>
                            <span>배출계수 조회 중</span>
                        </div>
                        <div v-else-if="factorError" class="elite-factor-error">
                            <i class="fas fa-exclamation-circle"></i>
                            <span>배출계수 정보를 불러올 수 없습니다.</span>
                        </div>
                        <div v-else-if="factorDetail && currentFactorValue" class="elite-factor-block">
                            <div class="elite-factor-header">
                                <div class="elite-factor-header-left">
                                    <h3 class="elite-factor-title">배출계수 상세 정보</h3>
                                </div>
                                <div class="elite-factor-badges">
                                    <span v-if="currentFactorValue.tier" class="elite-factor-tier">{{
                                        currentFactorValue.tier
                                    }}</span>
                                    <span v-if="currentFactorValue.startMonth" class="elite-factor-period">
                                        {{ formatPeriod(currentFactorValue.startMonth, currentFactorValue.endMonth) }}
                                    </span>
                                </div>
                            </div>
                            <div class="elite-factor-body">
                                <!-- GHG cards (CO2, CH4, N2O) -->
                                <div class="elite-ghg-grid">
                                    <div
                                        v-for="item in ghgCards"
                                        :key="item.componentCode"
                                        class="elite-ghg-card"
                                        :class="{ 'elite-ghg-card-co2': item.isCo2 }"
                                    >
                                        <div class="elite-ghg-head">
                                            <span class="elite-ghg-label">{{ item.label }}</span>
                                            <i
                                                class="elite-ghg-icon"
                                                :class="item.isCo2 ? 'fas fa-smog' : 'fas fa-flask'"
                                            ></i>
                                        </div>
                                        <div class="elite-ghg-value-wrap">
                                            <span class="elite-ghg-value">{{ formatNumber(item.emissionFactor) }}</span>
                                            <span class="elite-ghg-unit">{{ item.emissionUnit }}</span>
                                        </div>
                                    </div>
                                </div>
                                <!-- NCV / GCV row -->
                                <div v-if="calorificRows.length" class="elite-calorific-row">
                                    <div
                                        v-for="row in calorificRows"
                                        :key="row.componentCode"
                                        class="elite-calorific-cell"
                                    >
                                        <div class="elite-calorific-label-wrap">
                                            <div class="elite-calorific-label">{{ row.label }}</div>
                                            <div class="elite-calorific-sublabel">{{ row.sublabel }}</div>
                                        </div>
                                        <div class="elite-calorific-value-wrap">
                                            <span class="elite-calorific-value">{{
                                                formatNumber(row.emissionFactor)
                                            }}</span>
                                            <span class="elite-calorific-unit">{{ row.emissionUnit }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="currentFactorValue.evidenceFiles?.length" class="elite-factor-evidence">
                                    <i class="fas fa-paperclip"></i>
                                    <span>증빙 {{ currentFactorValue.evidenceFiles.length }}건</span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <!-- Section 4: Reference -->
                    <!-- <section
                        v-if="config.remarks || (factorDetail && factorDetail.remarks)"
                        class="elite-section elite-ref-section"
                    >
                        <div class="elite-ref-box">
                            <div class="elite-ref-left">
                                <div class="elite-ref-icon">
                                    <i class="fas fa-file-alt"></i>
                                </div>
                                <div>
                                    <h4 class="elite-ref-title">참조 사항</h4>
                                    <p class="elite-ref-text">
                                        {{ config.remarks || factorDetail?.remarks || "—" }}
                                    </p>
                                </div>
                            </div>
                            <div class="elite-ref-meta">
                                <div class="elite-ref-meta-label">설정 #</div>
                                <div class="elite-ref-meta-value">{{ config.configIdx }}</div>
                            </div>
                        </div>
                    </section> -->
                </div>
            </main>
        </div>
        <div v-else class="elite-loading">
            <p class="text-muted">데이터를 불러올 수 없습니다.</p>
        </div>

        <template #modal-footer>
            <div class="elite-footer-inner">
                <span class="elite-footer-meta text-muted mr-3">
                    <span class="mr-2">등록: {{ regDateLabel }}</span>
                    <span class="mr-2">수정: {{ modDateLabel }}</span>
                    <span v-if="config">작업자: {{ config.worker }}</span>
                </span>
                <b-button variant="secondary" @click="showModal = false">닫기</b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import CarbonCodes from "../../../codes";
import carbonApi from "../../../api";

export default {
    name: "ActivityConfigDetailPanel",
    props: {
        visible: { type: Boolean, default: false },
        config: { type: Object, default: null },
        loading: { type: Boolean, default: false },
    },
    data() {
        return {
            factorDetail: null,
            factorLoading: false,
            factorError: false,
        };
    },
    watch: {
        "config.factorIdx": {
            immediate: true,
            handler(factorIdx) {
                if (!factorIdx || !this.visible) {
                    this.factorDetail = null;
                    this.factorError = false;
                    return;
                }
                this.fetchFactor(factorIdx);
            },
        },
        visible(visible) {
            if (visible && this.config?.factorIdx) this.fetchFactor(this.config.factorIdx);
            if (!visible) {
                this.factorDetail = null;
                this.factorError = false;
            }
        },
    },
    computed: {
        currentFactorValue() {
            const values = this.factorDetail?.factorValues;
            if (!values || !values.length) return null;
            const start = this.config?.startMonth;
            if (start) {
                const matched = values.find((v) => v.startMonth <= start && (!v.endMonth || v.endMonth >= start));
                if (matched) return matched;
            }
            return values[0];
        },
        componentRows() {
            const v = this.currentFactorValue;
            const components = v?.components;
            if (!components || !components.length) return [];
            return components.map((c) => ({
                componentCode: c.componentCode,
                emissionFactor: c.emissionFactor,
                emissionUnit: c.emissionUnit || "",
            }));
        },
        /** GHG 성분 카드: API components 중 NCV/GCV 제외 → componentCode·emissionUnit 활용, 라벨은 GHG_SPECIES( gasCode ) 참조 */
        ghgCards() {
            const rows = this.componentRows || [];
            const calorificCodes = ["NCV", "GCV"];
            const ghgRows = rows.filter((r) => !calorificCodes.includes(r.componentCode));
            return ghgRows.map((r) => {
                const name = CarbonCodes.gasCode.getLabel(r.componentCode);
                const label = name && name !== r.componentCode ? `${r.componentCode} (${name})` : r.componentCode;
                return {
                    componentCode: r.componentCode,
                    label,
                    isCo2: r.componentCode === "CO2",
                    emissionFactor: r.emissionFactor ?? null,
                    emissionUnit: r.emissionUnit || "",
                };
            });
        },
        /** NCV, GCV: API components의 componentCode·emissionUnit 활용, 표시 순서 NCV → GCV */
        calorificRows() {
            const rows = (this.componentRows || []).filter((r) => ["NCV", "GCV"].includes(r.componentCode));
            const labelByCode = { NCV: "순발열량 (NCV)", GCV: "총발열량 (GCV)" };
            const sublabelByCode = { NCV: "Net Calorific Value", GCV: "Gross Calorific Value" };
            const order = ["NCV", "GCV"];
            const ordered = order.map((code) => rows.find((r) => r.componentCode === code)).filter(Boolean);
            return ordered.map((r) => ({
                componentCode: r.componentCode,
                label: labelByCode[r.componentCode] || r.componentCode,
                sublabel: sublabelByCode[r.componentCode] || "",
                emissionFactor: r.emissionFactor ?? null,
                emissionUnit: r.emissionUnit || "",
            }));
        },
        showModal: {
            get() {
                return this.visible;
            },
            set(val) {
                if (!val) this.$emit("close");
            },
        },
        ptAddrsDisplay() {
            const params = this.config?.apiParams;
            if (!params || !Array.isArray(params.ptAddrs) || params.ptAddrs.length === 0) return null;
            return params.ptAddrs.join(", ");
        },
        apiHeadersDisplay() {
            const h = this.config?.apiHeaders;
            if (!h) return null;
            if (typeof h === "string") return h;
            try {
                return typeof h === "object" ? JSON.stringify(h, null, 2) : String(h);
            } catch {
                return String(h);
            }
        },
        dataSourceLabel() {
            return CarbonCodes.dataSource.getLabel(this.config?.dataSource);
        },
        facilityTypeLabel() {
            return CarbonCodes.facilityType.getLabel(this.config?.facility?.facilityType);
        },
        preferredFuelLabel() {
            return CarbonCodes.fuelType.getLabel(this.config?.preferredFuel);
        },
        draftStatLabel() {
            return CarbonCodes.draftStat.getLabel(this.config?.draftStat);
        },
        collectionMethodLabel() {
            return CarbonCodes.collectionMethod.getLabel(this.config?.collectionMethod);
        },
        reportCycleLabel() {
            return CarbonCodes.reportCycle.getLabel(this.config?.reportCycle);
        },
        scopeTypeLabel() {
            return CarbonCodes.scopeType.getLabel(this.config?.category?.scopeType);
        },
        regDateLabel() {
            const d = this.config?.regDate;
            return d ? new Date(d).toLocaleDateString("ko-KR") : "-";
        },
        modDateLabel() {
            const d = this.config?.modDate;
            return d ? new Date(d).toLocaleDateString("ko-KR") : "-";
        },
    },
    methods: {
        async fetchFactor(factorIdx) {
            this.factorDetail = null;
            this.factorError = false;
            this.factorLoading = true;
            try {
                const res = await carbonApi.emissionFactor.getFactorById(factorIdx);
                const factor = res?.data?.data?.factor ?? res?.data?.factor ?? null;
                this.factorDetail = factor;
            } catch {
                this.factorError = true;
            } finally {
                this.factorLoading = false;
            }
        },
        formatPeriod(startMonth, endMonth) {
            if (!startMonth) return "";
            const s = String(startMonth);
            const start = s.length >= 6 ? `${s.slice(0, 4)}.${s.slice(4, 6)}` : s;
            if (!endMonth) return `${start} ~`;
            const e = String(endMonth);
            const end = e.length >= 6 ? `${e.slice(0, 4)}.${e.slice(4, 6)}` : e;
            return `${start} ~ ${end}`;
        },
        formatNumber(num) {
            if (num == null || num === "") return "—";
            const n = Number(num);
            if (Number.isNaN(n)) return String(num);
            return n.toLocaleString("ko-KR", {
                maximumFractionDigits: 4,
                minimumFractionDigits: 0,
                useGrouping: true,
            });
        },
        onHide() {
            this.$emit("close");
        },
    },
};
</script>

<style scoped>
/* 단일 루트 래퍼: 모달 본문 레이아웃(헤더/스크롤/푸터)을 우리가 렌더한 요소에 적용해 scoped가 동작하도록 함 */
.elite-detail-wrap {
    display: flex;
    flex-direction: column;
    max-height: 92vh;
    min-height: 0;
    background: #fff;
}

/* Loading (우리 컴포넌트가 렌더하는 루트이므로 scoped 적용됨) */
.elite-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    min-height: 160px;
    padding: 24px;
    color: #64748b;
}

.elite-loading p {
    margin: 0;
    font-size: 14px;
}

/* modal-header slot: 한 줄 제목+뱃지+닫기 (세로 여백 축소) */
.elite-header-inner {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
}

.elite-title-inner {
    font-size: 1rem;
    font-weight: 700;
    color: #334155;
    letter-spacing: -0.02em;
}

.elite-badges {
    display: flex;
    align-items: center;
    gap: 6px;
}

.elite-badge {
    padding: 2px 8px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.05em;
    border-radius: 4px;
}

.elite-badge-id {
    background: #f1f5f9;
    color: #64748b;
}

.elite-badge-active {
    background: #dcfce7;
    color: #047857;
    display: inline-flex;
    align-items: center;
}

.elite-badge-draft {
    background: #fef3c7;
    color: #b45309;
}

.elite-badge-inactive {
    background: #fee2e2;
    color: #b91c1c;
}

.elite-badge-method {
    background: #eff6ff;
    color: #2563eb;
}

.elite-badge-dot {
    width: 4px;
    height: 4px;
    background: #10b981;
    border-radius: 50%;
    margin-right: 6px;
}

.elite-header-close {
    color: #94a3b8;
    font-size: 1.25rem;
}

.elite-header-close:hover {
    color: #334155;
}

/* modal-footer slot */
.elite-footer-inner {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
}

/* Main */
.elite-main {
    flex: 1;
    overflow-y: auto;
    min-height: 0;
}

.elite-main.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}

.elite-main.custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f5f9;
}

.elite-main.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}

.elite-main.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

.elite-sections {
    padding: 16px 26px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Section */
.elite-section {
    margin: 0;
}

.elite-section-head {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
}

.elite-section-bar {
    width: 4px;
    height: 20px;
    background: #10b981;
    border-radius: 9999px;
}

.elite-section-title {
    font-size: 11px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.12em;
    margin: 0;
}

/* Facility grid */
.elite-facility-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
}

.elite-card {
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 16px;
}

.elite-card-label {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 4px;
}

.elite-card-value {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
}

/* Settings grid */
.elite-settings-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 24px;
}

.elite-setting {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.elite-setting-label {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
}

.elite-setting-value {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
}

.elite-setting-fuel {
    display: flex;
    align-items: center;
}

.elite-fuel-dot {
    width: 8px;
    height: 8px;
    background: #10b981;
    border-radius: 50%;
    margin-right: 8px;
}

.elite-settings-sub {
    margin-top: 20px;
    padding-top: 20px;
    border-top: 1px dashed #e2e8f0;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 24px;
}

.elite-setting-full {
    grid-column: 1 / -1;
}

.elite-mono {
    font-size: 12px;
    word-break: break-all;
}

.pre-wrap {
    white-space: pre-wrap;
}

/* Factor block */
.elite-factor-loading,
.elite-factor-error {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    font-size: 13px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    color: #64748b;
}

.elite-factor-error {
    color: #b91c1c;
    background: #fef2f2;
}

.elite-factor-block {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.elite-factor-header {
    background: #1e293b;
    padding: 10px 18px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
}

.elite-factor-header-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.elite-factor-icon {
    color: #34d399;
    font-size: 1rem;
}

.elite-factor-title {
    font-size: 12px;
    font-weight: 700;
    color: #fff;
    letter-spacing: 0.02em;
    margin: 0;
    line-height: 1.35;
}

.elite-factor-badges {
    display: flex;
    gap: 8px;
}

.elite-factor-tier {
    padding: 3px 8px;
    font-size: 10px;
    font-weight: 700;
    color: #fff;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    backdrop-filter: blur(4px);
    line-height: 1.2;
}

.elite-factor-period {
    padding: 3px 8px;
    font-size: 10px;
    font-weight: 700;
    color: #6ee7b7;
    background: rgba(16, 185, 129, 0.2);
    border: 1px solid rgba(16, 185, 129, 0.2);
    border-radius: 4px;
    line-height: 1.2;
}

.elite-factor-body {
    padding: 16px;
}

.elite-ghg-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 16px;
}

.elite-ghg-card {
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.elite-ghg-card-co2 {
    background: rgba(16, 185, 129, 0.08);
    border-color: #dcfce7;
}

.elite-ghg-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.elite-ghg-label {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.02em;
}

.elite-ghg-card-co2 .elite-ghg-label {
    color: #047857;
}

.elite-ghg-icon {
    font-size: 1.125rem;
    color: #cbd5e1;
}

.elite-ghg-card-co2 .elite-ghg-icon {
    color: rgba(16, 185, 129, 0.5);
}

.elite-ghg-value-wrap {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.elite-ghg-value {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
}

.elite-ghg-unit {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
}

.elite-calorific-row {
    padding-top: 8px;
    border-top: 1px solid #f1f5f9;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
}

.elite-calorific-cell {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    background: rgba(248, 250, 252, 0.8);
    border-radius: 8px;
}

.elite-calorific-label-wrap {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.elite-calorific-label {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
}

.elite-calorific-sublabel {
    font-size: 12px;
    color: #64748b;
}

.elite-calorific-value-wrap {
    text-align: right;
}

.elite-calorific-value {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1e293b;
}

.elite-calorific-unit {
    font-size: 10px;
    font-weight: 600;
    color: #94a3b8;
    margin-left: 4px;
}

.elite-factor-evidence {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 16px;
    font-size: 11px;
    color: #94a3b8;
}

/* Reference section */
.elite-ref-section {
    margin-bottom: 0;
}

.elite-ref-box {
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

@media (min-width: 768px) {
    .elite-ref-box {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
}

.elite-ref-left {
    display: flex;
    gap: 16px;
    align-items: flex-start;
}

.elite-ref-icon {
    width: 40px;
    height: 40px;
    background: #fff;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #94a3b8;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    flex-shrink: 0;
}

.elite-ref-title {
    font-size: 12px;
    font-weight: 700;
    color: #334155;
    margin: 0 0 4px 0;
}

.elite-ref-text {
    font-size: 12px;
    color: #64748b;
    line-height: 1.5;
    margin: 0;
    max-width: 32rem;
}

.elite-ref-meta {
    text-align: right;
}

.elite-ref-meta-label {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 0.08em;
}

.elite-ref-meta-value {
    font-size: 10px;
    color: #cbd5e1;
}

@media (max-width: 768px) {
    .elite-facility-grid {
        grid-template-columns: 1fr;
    }

    .elite-settings-grid {
        grid-template-columns: repeat(2, 1fr);
    }

    .elite-settings-sub {
        grid-template-columns: 1fr;
    }

    .elite-ghg-grid {
        grid-template-columns: 1fr;
    }

    .elite-calorific-row {
        grid-template-columns: 1fr;
    }
}
</style>

<!-- b-modal 기본 헤더/푸터 세로 여백 축소 (포탈된 DOM이라 별도 블록) -->
<style>
.activity-config-detail-modal .elite-detail-modal-header,
.activity-config-detail-modal .modal-header.elite-detail-modal-header {
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 16px;
    padding-right: 16px;
    align-items: center;
}

.activity-config-detail-modal .elite-detail-modal-footer,
.activity-config-detail-modal .modal-footer.elite-detail-modal-footer {
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 16px;
    padding-right: 16px;
}
</style>
